import ServiceBasketItem from '@/models/service-basket-item'
import MathTools from '@/helpers/math-tools'

/**
 * Defined structure of a service basket.
 * @export ServiceBasket
 * @class ServiceBasket
 */
export default class ServiceBasket {

    /**
     * Creates an instance of a ServiceBasketItem.
     * @param {Array<ServiceBasketItem>|Array<ServiceBasketAsJson>|Array<ServiceBasketSourceData>} items The items in the basket. If not provided an empty basket is created.
     * @memberof ServiceBasketItem
     */
    constructor(items = []) {

        /**
         * @type {Array<ServiceBasketItem>}
         */
        this.items = items
            ? items.map(sbi => ServiceBasketItem.ensure(sbi))
            : []
    }

    /**
     * @type {number}
     */
    get totalCost() {
        return this.items.length > 0
            ? this.items.map(sbi => sbi.cost)
                .reduce((acc, val) => acc + val)
            : 0
    }

    /**
     * @type {string}
     */
    get totalCostDisplay() {
        const totalCost = this.totalCost
        return totalCost > 0 ? MathTools.segmentCurrency(totalCost) : 'Free'
    }

    /**
     * @type {boolean}
     */
    get hasItems() {
        return this.items.length > 0
    }

    /**
     * Returns true if the service basket has an item with the specified serviceTypeId.
     * @param {number} id
     * @returns {boolean}
     */
    hasItem(id) {
        return this.items.map(({ serviceTypeId }) => serviceTypeId).includes(id)
    }

    /**
     * Returns the object in a form suitable to store.
     * @returns {ServiceBasketAsJson}
     * @memberof ServiceBasket
     */
    toStoreJson() {
        return {
            items: this.items
                .map(sbi => sbi.toStoreJson()),
        }
    }

    /**
     * Ensures that the provided data is already of this type. If not, it runs the constructor.
     * @param {ServiceBasket|ServiceBasketAsJson|ServiceBasketSourceData} data Source data - From API or state.
     * @returns {ServiceBasket} Either the data is passed back if already of this type or the result of the constructor is.
     * @memberof ServiceBasket
     */
    static ensure(data) {
        return data && data instanceof ServiceBasket
            ? data
            : new ServiceBasket(data.items)
    }

    /**
     * Returns the object type from raw stored JSON data.
     * @param {?ServiceBasketAsJson} data The stored data.
     * @memberof ServiceBasket
     */
    static fromStoreJson(data = null) {
        return data ? ServiceBasket.ensure(data) : null
    }
}
